import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * A hover card of label / value lines, for numbers that would crowd the line
 * they belong to (duration, cost, rage, satisfaction beside a transcript reply).
 *
 * Drawn like the trace's span hover card and the token / cost breakdown: raised
 * surface, a title row, then muted labels against primary values. Portaled to
 * <body> with fixed positioning so a scrolling drawer can't clip it.
 *
 *   rows: [{ label, value }] — `value` may be a node; falsy rows are skipped.
 */
export default function DetailHoverCard({ title, rows, children, className = '' }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const lines = (rows || []).filter(Boolean);

  const open = useCallback(() => {
    if (!lines.length || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const W = 232;
    const below = window.innerHeight - r.bottom;
    const placeAbove = below < 180 && r.top > below;
    const left = Math.min(Math.max(8, r.right - W), window.innerWidth - W - 8);
    setPos({ left, top: placeAbove ? null : r.bottom + 6, bottom: placeAbove ? window.innerHeight - r.top + 6 : null });
  }, [lines.length]);

  return (
    <span ref={ref} onMouseEnter={open} onMouseLeave={() => setPos(null)} className={`${lines.length ? 'cursor-help' : ''} ${className}`}>
      {children}
      {pos && createPortal(
        <div className="pointer-events-none fixed z-[130]" style={{ left: pos.left, top: pos.top ?? undefined, bottom: pos.bottom ?? undefined }}>
          <div className="w-[232px] overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] shadow-token-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
            {title ? (
              <div className="border-b border-[color:var(--border-subtle)] px-3 py-1.5 text-[12px] font-semibold text-[color:var(--text-primary)]">{title}</div>
            ) : null}
            <div className="space-y-0.5 px-3 py-2">
              {lines.map((l) => (
                <div key={l.label} className="flex items-baseline justify-between gap-3">
                  <span className="text-[11px] text-[color:var(--text-muted)]">{l.label}</span>
                  <span className="flex-none tabular-nums text-[11px] font-medium text-[color:var(--text-primary)]">{l.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
